import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Conversation, Message, MessagesState } from '../models/data';

const initialState: MessagesState = {
  conversations: [],
  currentConversation: null,
  messages: {},
  unreadCount: 0,
  isConnected: false,
  isTyping: {},
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    // Set all conversations
    setConversations(state, action: PayloadAction<{ conversations: Conversation[] }>) {
      state.conversations = action.payload.conversations;
    },

    // Add or update a single conversation
    addConversation(state, action: PayloadAction<{ conversation: Conversation }>) {
      const existingIndex = state.conversations.findIndex(
        (c) => c.id === action.payload.conversation.id
      );

      if (existingIndex >= 0) {
        state.conversations[existingIndex] = action.payload.conversation;
      } else {
        state.conversations.unshift(action.payload.conversation);
      }

      // Sort by updated_at
      state.conversations.sort((a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
    },

    // Set current conversation
    setCurrentConversation(state, action: PayloadAction<{ conversation: Conversation | null }>) {
      state.currentConversation = action.payload.conversation;
    },

    // Set messages for a conversation (replaces existing)
    setMessages(state, action: PayloadAction<{ conversationId: string; messages: Message[] }>) {
      state.messages[action.payload.conversationId] = action.payload.messages;
    },

    // Prepend older messages (for pagination)
    prependMessages(state, action: PayloadAction<{ conversationId: string; messages: Message[] }>) {
      const existing = state.messages[action.payload.conversationId] || [];
      state.messages[action.payload.conversationId] = [
        ...action.payload.messages,
        ...existing,
      ];
    },

    // Add a new message (real-time or sent)
    addMessage(state, action: PayloadAction<{ message: Message }>) {
      const conversationId = action.payload.message.conversation_id;

      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }

      // Check if message already exists (avoid duplicates)
      const exists = state.messages[conversationId].some(
        (m) => m.id === action.payload.message.id
      );

      if (!exists) {
        state.messages[conversationId].push(action.payload.message);
      }

      // Update conversation last_message and updated_at
      const conversationIndex = state.conversations.findIndex(
        (c) => c.id === conversationId
      );

      if (conversationIndex >= 0) {
        state.conversations[conversationIndex].last_message = {
          id: action.payload.message.id,
          content: action.payload.message.content,
          sender_id: action.payload.message.sender_id,
          created_at: action.payload.message.created_at,
        };
        state.conversations[conversationIndex].updated_at = action.payload.message.created_at;

        // Re-sort conversations
        state.conversations.sort((a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
      }
    },

    // Set total unread count
    setUnreadCount(state, action: PayloadAction<{ count: number }>) {
      state.unreadCount = action.payload.count;
    },

    // Increment unread count for a conversation
    incrementUnreadCount(state, action: PayloadAction<{ conversationId: string }>) {
      const conversation = state.conversations.find(
        (c) => c.id === action.payload.conversationId
      );

      if (conversation) {
        conversation.unread_count = (conversation.unread_count || 0) + 1;
        state.unreadCount += 1;
      }
    },

    // Clear unread count for a conversation
    clearUnreadCount(state, action: PayloadAction<{ conversationId: string }>) {
      const conversation = state.conversations.find(
        (c) => c.id === action.payload.conversationId
      );

      if (conversation) {
        state.unreadCount = Math.max(0, state.unreadCount - conversation.unread_count);
        conversation.unread_count = 0;
      }
    },

    // Set connection status
    setConnected(state, action: PayloadAction<{ isConnected: boolean }>) {
      state.isConnected = action.payload.isConnected;
    },

    // Set typing indicator for a conversation
    setTyping(state, action: PayloadAction<{ conversationId: string; isTyping: boolean }>) {
      state.isTyping[action.payload.conversationId] = action.payload.isTyping;
    },

    // Clear all messages state (on logout)
    clearMessages(state) {
      state.conversations = [];
      state.currentConversation = null;
      state.messages = {};
      state.unreadCount = 0;
      state.isConnected = false;
      state.isTyping = {};
    },
  },
});

export const messagesActions = messagesSlice.actions;
export default messagesSlice.reducer;
